import { Check, X, ArrowRight, Sparkles, ShieldCheck, Zap, CreditCard } from "lucide-react";
import { Link } from "wouter";
import { useTranslation } from "@/i18n/useTranslation";
import { Reveal } from "@/components/Reveal";

export function Pricing() {
  const { t } = useTranslation();

  // Order of values: free | cookieRobot Pro | Cookiebot | Usercentrics
  const compareRows: { label: string; free: string | boolean; cr: string | boolean; a: string | boolean; b: string | boolean }[] = [
    { label: t("comparison.col2"),  free: "10", cr: "100",  a: "100", b: "250" },
    { label: t("comparison.col3"),  free: "1",  cr: "4",    a: "1",   b: "2"   },
    { label: "An CI anpassbar",     free: false, cr: true,  a: true,  b: true  },
    { label: t("features.f2.title"), free: true, cr: true,  a: false, b: true  },
    { label: t("features.f1.title"), free: false, cr: true, a: true,  b: false },
    { label: t("features.f8.title"), free: false, cr: true, a: false, b: false },
    { label: t("features.f6.title"), free: false, cr: true, a: false, b: true  },
  ];

  const trustBadges = [
    { Icon: ShieldCheck, label: "DSGVO-konform" },
    { Icon: CreditCard,  label: "Keine Einrichtungsgebühr" },
    { Icon: Zap,         label: "Sofort startklar" },
  ];

  // 5 columns: label | Free | Pro | Cookiebot | Usercentrics
  const colWidths = "grid-cols-[1.5fr_1fr_1.1fr_1fr_1fr]";

  return (
    <section id="pricing" className="py-20 md:py-28 scroll-mt-16 bg-[#FAFAFA] border-y border-border">
      <div className="container mx-auto max-w-[1440px] px-5 md:px-6">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-14">
          <div className="text-xs font-semibold uppercase tracking-wider text-primary">
            Preise & Vergleich
          </div>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            {t("pricing.title")}
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            {t("comparison.subtitle")}
          </p>
        </div>

        {/* Unified pricing + comparison panel */}
        <Reveal>
          <div className="relative">
            {/* raised highlight behind CookieRobot Pro column - desktop */}
            <div
              aria-hidden="true"
              className="hidden md:block absolute top-0 bottom-0 left-[37.7%] w-[20.7%] rounded-2xl bg-white border-2 border-primary shadow-[0_30px_60px_-25px_rgba(28,100,164,0.35)]"
            />

            <div className="relative rounded-2xl border border-border bg-white overflow-hidden">
              {/* Header / pricing row */}
              <div className={`hidden md:grid ${colWidths} items-end`}>
                {/* col 1 — empty / label */}
                <div className="px-6 py-7" />

                {/* col 2 — CookieRobot Free */}
                <div className="px-4 py-7 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-3">
                    Einsteiger
                  </div>
                  <div className="text-base font-bold text-foreground">CookieRobot Free</div>
                  <div className="mt-3 flex items-baseline justify-center gap-1">
                    <span className="text-2xl font-semibold text-foreground tracking-tight">
                      {t("pricing.free.price")}
                    </span>
                    <span className="text-xs text-muted-foreground">/ Monat</span>
                  </div>
                  <a
                    href="https://admin.cookierobot.info/register"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-white px-3 py-2 text-xs font-semibold text-foreground border border-border hover:bg-muted transition-colors"
                  >
                    Kostenlos starten
                  </a>
                </div>

                {/* col 3 — CookieRobot Pro (featured) */}
                <div className="px-4 py-7 text-center relative">
                  <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider mb-3 shadow-sm">
                    <Sparkles className="w-3 h-3" />
                    {t("pricing.popular")}
                  </div>
                  <div className="text-base font-bold text-foreground">CookieRobot Pro</div>
                  <div className="mt-3 flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-foreground tracking-tight">
                      {t("pricing.pro.price")}
                    </span>
                    <span className="text-xs text-muted-foreground">/ Monat</span>
                  </div>
                  <Link
                    href="/shop"
                    className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground border border-[hsl(208,71%,30%)] hover:bg-[hsl(208,71%,34%)] transition-colors"
                  >
                    Pro kaufen <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>

                {/* col 4 — Cookiebot */}
                <div className="px-4 py-7 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-3">
                    Anbieter A
                  </div>
                  <div className="text-base font-medium text-foreground/80">Cookiebot</div>
                  <div className="mt-3 flex items-baseline justify-center gap-1 opacity-80">
                    <span className="text-2xl font-semibold text-foreground/70 tracking-tight">
                      9€
                    </span>
                    <span className="text-xs text-muted-foreground">/ Monat</span>
                  </div>
                  <div className="mt-4 h-[34px]" aria-hidden="true" />
                </div>

                {/* col 5 — Usercentrics */}
                <div className="px-4 py-7 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-3">
                    Anbieter B
                  </div>
                  <div className="text-base font-medium text-foreground/80">Usercentrics</div>
                  <div className="mt-3 flex items-baseline justify-center gap-1 opacity-80">
                    <span className="text-2xl font-semibold text-foreground/70 tracking-tight">
                      14€
                    </span>
                    <span className="text-xs text-muted-foreground">/ Monat</span>
                  </div>
                  <div className="mt-4 h-[34px]" aria-hidden="true" />
                </div>
              </div>

              {/* Mobile pricing header — simplified */}
              <div className="md:hidden p-6 border-b border-border bg-primary/[0.03]">
                <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider mb-3">
                  <Sparkles className="w-3 h-3" />
                  {t("pricing.popular")}
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-base font-bold text-foreground">CookieRobot Pro</span>
                  <span className="text-2xl font-bold text-primary">{t("pricing.pro.price")}</span>
                  <span className="text-xs text-muted-foreground">/ Monat</span>
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-sm text-muted-foreground">CookieRobot Free</span>
                  <span className="text-base font-semibold text-foreground">
                    {t("pricing.free.price")}
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <a
                    href="https://admin.cookierobot.info/register"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 rounded-md bg-white px-3 py-2.5 text-xs font-semibold text-foreground border border-border"
                  >
                    Kostenlos starten
                  </a>
                  <Link
                    href="/shop"
                    className="inline-flex items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2.5 text-xs font-semibold text-primary-foreground hover:bg-[hsl(208,71%,34%)] transition-colors"
                  >
                    Pro kaufen <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>

              {/* Comparison rows */}
              <div className="border-t border-border">
                {/* sub-header */}
                <div
                  className={`hidden md:grid ${colWidths} text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/30`}
                >
                  <div className="px-6 py-3 font-semibold">Funktionen im Vergleich</div>
                  <div className="px-4 py-3" />
                  <div className="px-4 py-3" />
                  <div className="px-4 py-3" />
                  <div className="px-4 py-3" />
                </div>

                {compareRows.map((r, i) => (
                  <div
                    key={i}
                    className={`md:grid ${colWidths} items-center text-sm border-b border-border last:border-b-0 ${
                      i % 2 === 1 ? "md:bg-muted/20" : ""
                    }`}
                  >
                    {/* mobile: label + 2x2 grid */}
                    <div className="md:hidden px-5 py-4">
                      <div className="text-[13px] font-semibold text-foreground mb-3">
                        {r.label}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <MobileCell name="Free" value={r.free} />
                        <MobileCell name="Pro" value={r.cr} highlight />
                        <MobileCell name="Cookiebot" value={r.a} />
                        <MobileCell name="Usercentrics" value={r.b} />
                      </div>
                    </div>

                    {/* desktop label */}
                    <div className="hidden md:block px-6 py-4 text-foreground/80 font-medium">
                      {r.label}
                    </div>

                    {/* desktop: Free */}
                    <div className="hidden md:block px-4 py-4 text-center text-foreground/70">
                      <Cell v={r.free} />
                    </div>

                    {/* desktop: Pro */}
                    <div className="hidden md:block px-4 py-4 text-center font-bold text-foreground">
                      <Cell v={r.cr} positive />
                    </div>

                    {/* desktop: Cookiebot */}
                    <div className="hidden md:block px-4 py-4 text-center text-foreground/70">
                      <Cell v={r.a} />
                    </div>

                    {/* desktop: Usercentrics */}
                    <div className="hidden md:block px-4 py-4 text-center text-foreground/70">
                      <Cell v={r.b} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* trust badges */}
        <div className="mt-10 flex flex-wrap justify-center items-center gap-x-8 gap-y-3 text-xs text-muted-foreground">
          {trustBadges.map(({ Icon, label }, i) => (
            <div key={i} className="inline-flex items-center gap-1.5">
              <Icon className="w-3.5 h-3.5 text-primary/70" />
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MobileCell({
  name,
  value,
  highlight = false,
}: {
  name: string;
  value: string | boolean;
  highlight?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5 ${
        highlight ? "border-primary/40 bg-primary/[0.04]" : "border-border bg-white"
      }`}
    >
      <span
        className={`text-[11px] font-semibold uppercase tracking-wider ${
          highlight ? "text-primary" : "text-muted-foreground"
        }`}
      >
        {name}
      </span>
      <span className={highlight ? "font-bold text-foreground" : "text-foreground/80"}>
        <Cell v={value} positive={highlight} />
      </span>
    </div>
  );
}

function Cell({ v, positive = false }: { v: string | boolean; positive?: boolean }) {
  if (typeof v === "boolean") {
    return v ? (
      <span
        className={`inline-flex w-6 h-6 items-center justify-center rounded-full ${
          positive ? "bg-primary/10 text-primary" : "bg-emerald-50 text-emerald-600"
        }`}
      >
        <Check className="w-3.5 h-3.5" strokeWidth={3} aria-hidden="true" />
        <span className="sr-only">enthalten</span>
      </span>
    ) : (
      <span className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-muted text-muted-foreground/60">
        <X className="w-3.5 h-3.5" aria-hidden="true" />
        <span className="sr-only">nicht enthalten</span>
      </span>
    );
  }
  return <span>{v}</span>;
}
